import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowUpRight } from "lucide-react";
import { MaskLine, FadeUp } from "./Reveal";
import { CONTACT } from "../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Estate Vineyard Management",
  "Vineyard Development / Replanting",
  "Vineyard Data & Reporting",
  "Property Acquisition / Due Diligence",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", location: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you. We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", interest: "", location: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 md:py-40 bg-[#F3EFE6] hairline-t">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 md:gap-10">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#B89B67] font-semibold mb-10">Contact</p>
          </FadeUp>
          <h2 className="font-display font-light text-[#1A3626] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            <MaskLine delay={0.1}>Start a</MaskLine>
            <MaskLine delay={0.22}>conversation.</MaskLine>
          </h2>
          <FadeUp delay={0.35} className="mt-10 max-w-[42ch]">
            <p className="text-base leading-relaxed text-[#2C2926]/80">
              Whether you own a vineyard, are planting one, or are evaluating a property in Napa or Sonoma, we'd like to hear about it.
            </p>
          </FadeUp>
          <FadeUp delay={0.45} className="mt-14">
            <div className="flex items-center gap-4 text-sm text-[#2C2926]/80">
              <Mail size={15} className="text-[#B89B67]" strokeWidth={1.5} />
              <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="hover:text-[#1A3626] transition-colors duration-300">{CONTACT.email}</a>
            </div>
          </FadeUp>
        </div>

        <div className="md:col-span-7">
          <FadeUp delay={0.25}>
            <form data-testid="contact-form" onSubmit={submit} className="bg-[#F9F6F0] border border-[#E5DEC9] p-8 md:p-14">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-name">Name *</label>
                  <input id="f-name" data-testid="contact-name-input" required className="input-stationery" placeholder="Your full name" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-email">Email *</label>
                  <input id="f-email" data-testid="contact-email-input" required type="email" className="input-stationery" placeholder="you@example.com" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-phone">Phone</label>
                  <input id="f-phone" data-testid="contact-phone-input" className="input-stationery" placeholder="(707) 000-0000" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-interest">I'm interested in</label>
                  <select id="f-interest" data-testid="contact-interest-select" className="input-stationery" value={form.interest} onChange={set("interest")}>
                    <option value="">Select…</option>
                    {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-location">Vineyard / Property Location</label>
                  <input id="f-location" data-testid="contact-location-input" className="input-stationery" placeholder="e.g. Oakville, Napa Valley" value={form.location} onChange={set("location")} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] tracking-[0.26em] uppercase text-[#364132]/60 font-semibold mb-1" htmlFor="f-message">Message *</label>
                  <textarea id="f-message" data-testid="contact-message-input" required rows={4} className="input-stationery resize-none" placeholder="Tell us about your vineyard or the property you're considering." value={form.message} onChange={set("message")} />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-12 inline-flex items-center gap-3 bg-[#1A3626] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-[#B89B67] hover:text-[#12271B] active:scale-95 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send enquiry"}
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
